import React, { useState, useEffect, useContext, useRef } from "react";
import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    Avatar,
    Pagination,
    CircularProgress,
    Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CustomizedTables, { StyledTableCell, StyledTableRow } from "./../../../Component/UI/Table/Table";
import BrandAvatar from "../../../Component/Avatar/Avatar"
import Modal from "../../../Component/Modal/Modal";
import classes from "./HistoryV2.module.css";
import useHistory from "./useHistory";
import { DiscoveryContext } from "./../discovery-context";
import { AuthContext } from "../../../context/auth-context";
import { OVERVIEW_LLM_LABELS } from "../../../utils/constant";
import api from "../../../BrandOnBoarding/api";
import { updateValidatedSources } from "../../../utils/helpers"
import { getMetricsRecord } from "../../../utils/constant"
import { ENTITY_LABELS, getEntityLabel } from "../../../utils/labelMapping";

const PAGE_LIMIT = 5;

// signalStrength's "pending validation" sources can arrive as a plain URL string
// (old/legacy), a per-URL {url, count, http_status} object, or an already-merged
// {url: count, ...} map (v4's live socket push) — normalize each to a single-key
// {url: count} entry (or pass an already-merged map through as-is) so the reducer's
// merge-by-spread logic works regardless of which shape the backend sent.
function toOldSourcesEntry(entry) {
  if (typeof entry === "string") return { [entry]: 1 };
  if (entry && typeof entry === "object" && "url" in entry) {
    return { [entry.url]: entry.count ?? 1 };
  }
  return entry;
}

const HistoryV2 = ({
    filter = "brand",
    setCancelTransaction = () => { },
    startPolling = () => { },
    stopPolling = () => { },
    setActiveTab = () => { },
    setDomain = () => { },
    setIsAuditRunning = () => { },
    processRetry = false,
    open,
    onClose
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [filterOption, setFilterOption] = useState(filter);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchTotalCount, setSearchTotalCount] = useState(0);
    const [searchTotalPages, setSearchTotalPages] = useState(0);
    const [isSearchMode, setIsSearchMode] = useState(false);

    const discoveryCtx = useContext(DiscoveryContext);
    const { user, selectedCustomerId } = useContext(AuthContext);
    const transactionIdRef = useRef();
    const debounceTimerRef = useRef(null);
    const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
    const discoveryType = discoveryCtx.type ?? "brand";
    const metricsRecord = getMetricsRecord();
    const {
        history,
        setHistory,
        loading,
        getHistory,
        deleteHistory,
        error,
        totalCount,
        totalPages,
    } = useHistory(filterOption);
    const { page, setPage } = discoveryCtx;

    const state = discoveryCtx.state;

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 400);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchQuery]);

    useEffect(() => {
        const performSearch = async () => {
            if (!debouncedSearchQuery.trim()) {
                setIsSearchMode(false);
                setSearchResults([]);
                setSearchTotalCount(0);
                setSearchTotalPages(0);
                return;
            }

            setIsSearchMode(true);
            setSearchLoading(true);

            try {
                const searchUrl = filterOption === "categoryAnalysis"
                    ? `${webSocketBaseURL}/search-history/categoryAnalysis/brand/${selectedCustomerId}/${user?.user_role?.user_id}`
                    : `${webSocketBaseURL}/search-history/brandAudit/${filterOption}/${selectedCustomerId}/${user?.user_role?.user_id}`;
                const response = await api.get(searchUrl, {
                    params: {
                        search: debouncedSearchQuery,
                        page: page,
                        limit: PAGE_LIMIT
                    }
                });
                console.log("Searchresponse", response)
                const { data = [], totalCount = 0, totalPages = 0 } = response.data;
                discoveryCtx.updateTransactionHistory(data)
                // No transformation needed - search API returns same format as history API
                setSearchResults(data);
                setSearchTotalCount(totalCount);
                setSearchTotalPages(totalPages);
            } catch (error) {
                console.error("Search error:", error);
                setSearchResults([]);
                setSearchTotalCount(0);
                setSearchTotalPages(0);
            } finally {
                setSearchLoading(false);
            }
        };

        performSearch();
    }, [debouncedSearchQuery, page, filterOption, selectedCustomerId, user?.user_role?.user_id, webSocketBaseURL]);

    useEffect(() => {
        setFilterOption(filter);
        setPage(1);
        setSearchQuery("");
        setDebouncedSearchQuery("");
        setIsSearchMode(false);
        setSearchResults([]);
    }, [filter, processRetry]);

    useEffect(() => {
        if (isSearchMode) return;

        if (!searchQuery.trim()) {
            getHistory(page, PAGE_LIMIT);
        }
    }, [page, filterOption, isSearchMode]);

    // Update history from context
    useEffect(() => {
        const contextHistory = discoveryCtx?.state?.transactionHistory;
        if (Array.isArray(contextHistory)) {
            setHistory(contextHistory);
        }
    }, [discoveryCtx.state?.transactionHistory]);

    // Update transactionId ref
    useEffect(() => {
        if (discoveryCtx.state.transactionId) {
            transactionIdRef.current = discoveryCtx.state.transactionId;
        }
    }, [discoveryCtx.state.transactionId]);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchQuery(value);
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchQuery("");
        setDebouncedSearchQuery("");
        setIsSearchMode(false);
        setSearchResults([]);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);

        // Scroll to top of modal when page changes
        const tableWrapper = document.querySelector(`.${classes.tableWrapper}`);
        if (tableWrapper) {
            tableWrapper.scrollTop = 0;
        }
    };

    const refreshHistory = () => {
        setHistory([]);
        setPage(1);
        setSearchQuery("");
        setDebouncedSearchQuery("");
        setIsSearchMode(false);
        setSearchResults([]);
        getHistory(1, PAGE_LIMIT);
    };

    const deleteHistoryItemHandler = () => {
        if (itemToDelete) {
            const deletedId = String(itemToDelete.transactionId ?? "");
            const activeIds = [discoveryCtx.state?.id, discoveryCtx.state?.transactionId]
                .filter(Boolean)
                .map(String);
            const isDeletingActiveRecord = activeIds.includes(deletedId);

            if (isDeletingActiveRecord) {
                setCancelTransaction(true);
                stopPolling();
                discoveryCtx.resetState();
            }
            deleteHistory(itemToDelete.transactionId);
            setShowDeleteDialog(false);
            setItemToDelete(null);

            setTimeout(() => {
                if (isSearchMode) {
                    setDebouncedSearchQuery("");
                    setTimeout(() => setDebouncedSearchQuery(searchQuery), 100);
                } else {
                    getHistory(page, PAGE_LIMIT);
                }
            }, 500);
        }
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setShowDeleteDialog(true);
    };

    let currentBrandSnippets = [];
    let currentBrandThemes = [];
    const coreAttributeMap = {};

    // handleSocketData function remains the same...
    const handleSocketData = (
        socketResponse,
        transaction,
        transactionId,
        showOldSourcesRecord
    ) => {
        if (!socketResponse || typeof socketResponse !== "object") {
            console.error("Invalid socket response:", socketResponse);
            return;
        }

        let { type, request, response, category, llm, market, status, isValidated, persona } = socketResponse;
        if (!type || !request) {
            console.error("Incomplete socket response:", response);
            return;
        }
        if (status !== "COMPLETED") {
            return false;
        }
        const currentContextTxnId = transactionIdRef.current;
        if (transactionId !== currentContextTxnId) return;
        const onlyIfValidated = (data, empty = Array.isArray(data) ? [] : {}) => {
            if (showOldSourcesRecord) {
                if (Array.isArray(data)) return data;
                if (data == null) return [];
                return [data];
            }

            return isValidated ? data : empty;
        };
        const commonReq = {
            category: category,
            llm: llm,
            market: market,
            persona: persona
        };

        switch (type) {
            case "rank":
                if (!response) return console.warn("Missing response for rank");
                response = { ...response, request_id: socketResponse.id, isValidated };
                const sourcesRank = onlyIfValidated(response?.sources?.rank || [], []);
                discoveryCtx.updateRankScores({ req: commonReq, res: response });
                discoveryCtx.updateSources({ res: sourcesRank, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
                break;

            case "sentiment":
                if (!response || !transaction)
                    return console.warn("Missing response/Transaction for sentiment");

                const entity = { ...response?.data }?.[0];
                const sentimentSources = onlyIfValidated(response?.sources?.sentiment || [], []);
                const updatedExtras = !showOldSourcesRecord ? updateValidatedSources(response.extras) : response.extras;
                const brandRes = response?.error
                    ? { error: "something went wrong" }
                    : {
                        name: entity?.name || "Unknown",
                        favorability: entity?.score ?? 0,
                        extras: updatedExtras,
                        sentimentSources,
                        isValidated,
                        rbiType: response?.rbiType,
                    };

                let index = -1;
                try {
                    transaction.competitor?.[request.category]?.forEach((item, idx) => {
                        if (item.name?.toLowerCase() == entity?.name?.toLowerCase()) {
                            index = idx;
                        }
                    });
                    if (transaction.brand === entity?.name) index = -1;
                } catch (err) {
                    console.warn("Error calculating sentiment index:", err);
                }

                discoveryCtx.updateBrandScores({
                    req: commonReq,
                    res: { data: brandRes, index: index + 1 },
                    sentimentSources,
                    isValidated
                });
                discoveryCtx.updateSources({ res: sentimentSources, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
                break;


            case "signalStrength":
                if (!response) return console.warn("Missing response for signalStrength");
                const negativeAttributes = response?.aggregatedNegatives || [];
                const positiveAttributes = response?.aggregatedPositives || [];

                let sourcesPositiveArray = onlyIfValidated(
                    response?.aggregatedPositiveSources || [],
                    []
                );
                let sourcesNegativeArray = onlyIfValidated(
                    response?.aggregatedNegativeSources || [],
                    []
                );

                if (showOldSourcesRecord) {
                    sourcesPositiveArray = sourcesPositiveArray.map(toOldSourcesEntry);
                    sourcesNegativeArray = sourcesNegativeArray.map(toOldSourcesEntry);
                }

                discoveryCtx.updatePositiveNegativeAttributes({
                    res: {
                        name: socketResponse.brand,
                        positives: positiveAttributes,
                        negatives: negativeAttributes,
                        baseResponses:response?.baseResponses || {}
                    },
                    req: commonReq
                });

                discoveryCtx.updateSources({ res: sourcesPositiveArray, req: commonReq, showOldSourcesRecord, type: "Positive Attributes" });
                discoveryCtx.updateSources({ res: sourcesNegativeArray, req: commonReq, showOldSourcesRecord, type: "Negative Attributes" });
                break;

            case "categoryDimension":
                if (!response)
                    return console.warn("Missing response for categoryDimension");
                const normalize = (src) =>
                    Array.isArray(src)
                        ? onlyIfValidated(src, [])
                        : onlyIfValidated(src ? [src] : [], []);

                const sourcesCategory = normalize(response?.sources?.category);
                const sourcesDim = normalize(response?.sources?.dimension);

                const sourcesCategoryDim = [...sourcesCategory, ...sourcesDim];
                discoveryCtx.updateCategoryDimensions({
                    ...commonReq,
                    data: response?.data,
                    summary: response?.summary || "",
                    sources: sourcesCategory,
                    baseResponses: { ...(response?.baseResponses || {}), multi_prompting: response?.multi_prompting || false, num_runs: response?.num_runs },
                    isValidated
                });
                discoveryCtx.updateSources({ res: sourcesCategoryDim, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
                break;

            case "taxonomy":
                if (!response) return console.warn("Missing response for taxonomy");
                response = { ...response, isValidated };
                discoveryCtx.updatedTaxonomies({
                    res: response,
                    req: commonReq
                });
                break;

            case "core-attribute":
                if (!response || !category || !llm)
                    return console.warn("Missing data for core-attribute");
                const key = `${llm}__${category}_${persona}`;

                if (!coreAttributeMap[key]) {
                    coreAttributeMap[key] = [];
                }

                coreAttributeMap[key].push(response);
                let competitors = transaction?.competitor?.[category]?.map(
                    (item) => item.name
                );
                let brand = transaction?.competitor?.["default"]?.[0].name;
                if (competitors) {
                    competitors.unshift(brand);
                } else {
                    competitors = [];
                }
                const expectedCount =
                    (transaction?.competitor?.['default']?.length ?? 0) +
                    (transaction?.competitor?.[category]?.length ?? 0);

                if (coreAttributeMap[key].length === expectedCount) {
                    const group = coreAttributeMap[key];
                    let coreAttributeSummary = "";
                    const products = [];
                    const sources = [];

                    const category_averages = group[0]?.category_averages;

                    for (const res of group) {
                        if (!res?.error && Array.isArray(res?.products)) {
                            coreAttributeSummary += res.products?.[0]?.summary || "";
                            coreAttributeSummary += "\n\n";
                            const productSources = onlyIfValidated(res?.sources?.product || [], []);
                            products.push({
                                ...res.products?.[0],
                                brandName: res.brandName,
                                sources: productSources,
                                baseResponses: res.baseResponses || null,
                                multi_prompting: res.multi_prompting || false,
                                num_runs: res.num_runs,
                            });
                            if (showOldSourcesRecord || isValidated) {
                                const normalize = (src) =>
                                    Array.isArray(src)
                                        ? src
                                        : src
                                            ? [src]
                                            : [];

                                const categorySources = normalize(res?.sources?.category);
                                const productSources = normalize(res?.sources?.product);

                                sources.push(...categorySources, ...productSources);
                            }
                        }
                    }

                    let sortedProducts = [];
                    let baseResponsesArray = [];
                    for (const company of competitors) {
                        const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
                        for (const product of products) {
                            const normalizedProductName = (product?.brandName || product?.name || "")?.toLowerCase()?.replace(/\s+/g, "");
                            if (normalizedProductName === normalizedCompany) {
                                // Extract baseResponses and add to separate array
                                if (product.baseResponses && typeof product.baseResponses === 'object') {
                                    const baseResObj = product.baseResponses;
                                    if (baseResObj.baseResponses && Array.isArray(baseResObj.baseResponses)) {
                                        const firstItem = baseResObj.baseResponses[0];
                                        const isPerAttributeMulti = firstItem?.attribute != null && Array.isArray(firstItem?.runs);
                                        const isFlatRunMulti = firstItem?.run != null && firstItem?.baseRes != null && baseResObj.baseResponses.length > 1;
                                        const isMultiPrompting = product.multi_prompting === true || isPerAttributeMulti || isFlatRunMulti;
                                        baseResponsesArray.push({
                                            brandName: product.brandName,
                                            basePrompt: baseResObj.basePrompt || "",
                                            multi_prompting: isMultiPrompting,
                                            num_runs: product.num_runs || (isPerAttributeMulti ? firstItem?.runs?.length : undefined),
                                            baseResponse: isPerAttributeMulti
                                                ? baseResObj.baseResponses
                                                : isFlatRunMulti
                                                    ? baseResObj.baseResponses
                                                    : baseResObj.baseResponses.map(item => ({ baseRes: item.baseRes || "" }))
                                        });
                                    }
                                }

                                // Remove baseResponses from product before adding to sortedProducts
                                const { baseResponses, ...productWithoutBaseResponses } = product;
                                sortedProducts.push(productWithoutBaseResponses); 
                                break;
                            }
                        }
                    }

                    discoveryCtx.updateCoreAttributes({
                        category_averages,
                        products: sortedProducts,
                        summary: coreAttributeSummary,
                        baseResponses: baseResponsesArray,
                        ...commonReq,
                        isValidated
                    });

                    discoveryCtx.updateSources({ res: sources, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });

                    delete coreAttributeMap[key];
                }
                break;

            case "conversations":
                if (!response) return console.warn("Missing response for 'conversations'");
                discoveryCtx.updateConversations({
                    data: response || {},
                    category: request?.category,
                    llm,
                    market,
                    persona,
                    request_id: socketResponse.id,
                    created_at: socketResponse?.created_at,
                    conversation_id:socketResponse?.conversationID,
                    conversationStatus:socketResponse?.conversationStatus,
                    isValidated
                });
                break;

            case "contentMapping":
                if (!response)
                    return console.warn("Missing response for 'conversations'");

                discoveryCtx.updateContentMapping({
                    data: response || {},
                    category: request?.category,
                    llm: request?.llm,
                    request_id: socketResponse.id
                });
                break;

            case "description":
                if (!response?.data)
                    return console.warn("Missing response.data for description");
                discoveryCtx.updateBrandDescription({
                    res: response.data,
                    req: { category: category, llm: llm }
                });
                break;

            case "theme":
            case "snippet": {
                const isTheme = type === "theme";
                const dataSnippet = response?.data?.llm ? response?.data : response?.data?.data;

                if (!Array.isArray(response?.data?.data)) {
                    console.warn(`${type} response is not a valid array:`, dataSnippet);
                    return;
                }

                if (!llm || !category) {
                    console.warn("Missing llm/category for", type);
                    return;
                }

                const targetStore = isTheme ? currentBrandSnippets : currentBrandThemes;
                const contextMethod = isTheme
                    ? discoveryCtx.updateBrandSnippets
                    : discoveryCtx.updatedBrandThemes;

                if (!targetStore[llm]) targetStore[llm] = {};
                if (!targetStore[llm][category]) targetStore[llm][category] = [];

                targetStore[llm][category] = [
                    ...targetStore[llm][category],
                    dataSnippet,
                ];
                contextMethod({
                    res: targetStore[llm][category],
                    req: { category, llm }
                });

                break;
            }

            default:
                console.warn(`Unhandled socket data type: ${type}`);
        }
    };

    const getTransactionData = async (transactionRecord) => {
        onClose();
        discoveryCtx.resetState();
        discoveryCtx.updateLoading(true);
        const completedTests = transactionRecord.testIds
            .filter(t => t.status !== "INACTIVE");
        const transactionId = completedTests[completedTests.length - 1]?.transactionId;
        setActiveTab(completedTests.length - 1);
        setIsAuditRunning(false);
        setDomain(transactionRecord.brandDomainName)
        stopPolling();
        transactionRecord?.testIds
            .map((item, originalIndex) => ({ ...item, originalIndex }))
            .filter(item => item.status !== "INACTIVE")
            .forEach(item => {
                if (transactionRecord?.transactionId === item.transactionId) {
                    discoveryCtx.updateLaunchRecord({
                        name: filterOption === "categoryAnalysis" ? "Final Overview" : `Final ${ENTITY_LABELS.audit}`,
                        transactionId: item.transactionId,
                    });
                } else {
                    discoveryCtx.updateLaunchRecord({
                        name: filterOption === "categoryAnalysis"
                            ? `Test Overview ${item.originalIndex + 1}`
                            : `Test ${ENTITY_LABELS.audit} ${item.originalIndex + 1}`,
                        transactionId: item.transactionId,
                        totalTestAudit: transactionRecord?.testIds?.length
                    });
                }
            });
        discoveryCtx.updateTransactionId(transactionId);
        discoveryCtx.updateConversationEnabled(transactionRecord.conversation_enabled ?? false);
        discoveryCtx.updateFinalTransactionId(transactionRecord.transactionId)
        discoveryCtx.cancelSelection();
        const entityLabel = filterOption === "categoryAnalysis"
            ? (transactionRecord?.category?.[0] || "")
            : transactionRecord.brand;
        discoveryCtx.updateEntity(entityLabel);
        let competitors = transactionRecord.competitor;
        competitors["default"] = filterOption === "categoryAnalysis"
            ? [{ name: entityLabel, domain: "example.com", variants: [], display_name: entityLabel }]
            : [{ name: transactionRecord.brand, domain: transactionRecord.brandDomainName, variants: [], display_name: transactionRecord.brand }];
        discoveryCtx.updatePrimaryLLM(transactionRecord?.primaryLLM);
        discoveryCtx.updatedCompetitors(competitors);
        discoveryCtx.updateMarket(
            transactionRecord?.market?.length > 0
                ? transactionRecord?.market
                : ["General Market"]
        );
        discoveryCtx.updatePersona(
            transactionRecord?.persona?.length > 0
                ? transactionRecord?.persona
                : [{ title: "General Persona", description: "General Persona" }]
        );
        discoveryCtx.updateCategory(transactionRecord?.category);
        discoveryCtx.updateSelectedLLM(transactionRecord?.primaryLLM);
        discoveryCtx.updateSourceValidationStatus(transactionRecord?.sourceValidationStatus);
        discoveryCtx.updateSelectedMarket(transactionRecord?.market[0]);
        discoveryCtx.updateSelectedCategory(transactionRecord?.category[0]);
        discoveryCtx.updateSelectedPersona(Array.isArray(transactionRecord?.persona) &&
            transactionRecord?.persona?.length > 0
            ? transactionRecord?.persona[0]?.title ?? transactionRecord?.persona[0]
            : "General Persona");
        discoveryCtx.updateCustomTemplateID(transactionRecord?.userMetricSettingId || null);
        if (transactionRecord?.conversationSettings) {
        discoveryCtx.updateConversationSetting(transactionRecord.conversationSettings);
        }
        discoveryCtx.updateRbiType(transactionRecord?.rbiType || "propensityToRecommend");
        if (transactionRecord?.keywords) {
            discoveryCtx.updateKeywords(transactionRecord.keywords);
        }
        discoveryCtx.updateLLM(transactionRecord?.llm);
        discoveryCtx.updateName(
            filterOption === "categoryAnalysis"
                ? `${entityLabel} Category Analysis Overview`
                : `${transactionRecord?.brand} ${ENTITY_LABELS.brand} Overview`
        );
        discoveryCtx.updateId(transactionId);
        discoveryCtx.updatedLaunched(true);

        setTimeout(() => {
            api
                .get(
                    filterOption === "categoryAnalysis"
                        ? `${webSocketBaseURL}/transaction/categoryAnalysis/${user?.user_role?.user_id}/${transactionId}`
                        : `${webSocketBaseURL}/transaction/brandAudit/${user?.user_role?.user_id}/${transactionId}`
                )
                .then((response) => {
                    const { totalCount, completedTaskCount, transactionStatus, task = [], sourceValidationStatus, finalTransactionIdStatus, brandAuditLimits } = response?.data;
                    discoveryCtx.updateStatus({ totalCount, completedTaskCount });
                    discoveryCtx.updateLaunch(brandAuditLimits)
                    discoveryCtx.setMetricSettingObj(response?.data?.user_metric_setting_object ?? "");
                    discoveryCtx.updateFormData(true);
                    const showOldSourcesRecord = sourceValidationStatus === "PENDING";
                    discoveryCtx.updateTransactionStatus(transactionStatus);
                    const keys = ["applyPersonaToAllCategory", "applyPersonaToAllMarkets"];
                    const personaSettings = keys.reduce((acc, key) => {
                        acc[key] = task[0]?.request_object?.[key] ?? false;
                        return acc;
                    }, {});

                    discoveryCtx.updatePersonaSettings(personaSettings);
                    discoveryCtx.updateCategoryType(task[0]?.request_object?.categoryType || {});
                    discoveryCtx.updateRankCompetitiveSet(task[0]?.request_object?.rank_competitive_set || {});
                    discoveryCtx.updateRankMethodology(task[0]?.request_object?.rank_methodology || "by_category");
                    const llmBatchConfig = task[0]?.request_object?.llmBatchConfig;
                    discoveryCtx.updateIsBatchMode(!!llmBatchConfig && Object.keys(llmBatchConfig).length > 0);
                    discoveryCtx.updateIsNonBrandedQuestions(!!task[0]?.request_object?.is_non_branded_questions);
                    discoveryCtx.updateIsFactBasedQuestions(!!task[0]?.request_object?.is_fact_based_questions);

                    discoveryCtx.setMetricSettingObj(response?.data?.user_metric_setting_object ?? {})
                    discoveryCtx.updateFinalTransactionStatus(finalTransactionIdStatus);
                    if (transactionStatus === "RUNNING") {
                        setIsAuditRunning(true);
                        discoveryCtx.updateLaunchTransaction(true);
                        startPolling(transactionId, discoveryCtx.type);
                    } else {
                        discoveryCtx.updateLoading(false);
                    }

                    for (const record of task) {
                        handleSocketData(record, transactionRecord, transactionId, showOldSourcesRecord);
                    }
                });
        }, 1000);
    };

    const handleViewDetails = (item) => {
        getTransactionData(item);
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            COMPLETED: { color: "success", label: "Completed" },
            RUNNING: { color: "warning", label: "In Progress" },
            PENDING: { color: "warning", label: "In Progress" },
            INITIATED: { color: "warning", label: "In Progress" },
            RETRY: { color: "error", label: "Failed" },
            FAILED: { color: "error", label: "Failed" },
        };

        const config = statusConfig[status] || { color: "default", label: status };

        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                className={classes.statusChip}
            />
        );
    };

    // Run Type pill — same color pairing used for Discovery's "Discovery Type" column
    // (Full/production = blue, Test/preliminary = violet)
    const getRunTypeChip = (auditType) => {
        const isTest = (auditType || "").toLowerCase() === "test";
        return (
            <Chip
                label={isTest ? "Test" : "Final"}
                size="small"
                sx={{
                    fontSize: "11px",
                    height: "22px",
                    backgroundColor: isTest ? "#ede9fe" : "#e0f2fe",
                    color: isTest ? "#6d28d9" : "#0369a1",
                    fontWeight: 500,
                }}
            />
        );
    };

    // Test Runs circular badge — red dot indicates at least one failed run
    const getTestRunsBadge = (count, hasFailure) => {
        if (count == null) return <span>-</span>;
        return (
            <Box sx={{ position: "relative", display: "inline-flex" }}>
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: "#e5e7eb",
                        color: "#374151",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 700,
                    }}
                >
                    {count}
                </Box>
                {hasFailure && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#dc3545",
                            border: "1.5px solid #fff",
                        }}
                    />
                )}
            </Box>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    function capitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const headers = [
        { id: "brand", name: `${getEntityLabel(discoveryType)}`, sorting: false },
        { id: "category", name: "Category", sorting: false },
        { id: "llm", name: "LLM Model", sorting: false },
        { id: "market", name: "Market", sorting: false },
        { id: "persona", name: "Persona", sorting: false },
        { id: "date", name: "Date Run", sorting: false },
        { id: "testRuns", name: "Test Runs", sorting: false },
        { id: "runType", name: "Run Type", sorting: false },
        { id: "status", name: "Status", sorting: false },
        { id: "actions", name: "Actions", sorting: false },
    ];

    // Helper function to render array items with overflow indicator
    const renderArrayWithOverflow = (items, maxVisible = 3) => {
        if (!Array.isArray(items) || items.length === 0) {
            return null;
        }

        const displayItems = items.map(item =>
            typeof item === "string" ? item : item?.title || item?.name || String(item)
        );

        const visibleItems = displayItems.slice(0, maxVisible);
        const remainingItems = displayItems.slice(maxVisible);
        const remainingCount = remainingItems.length;

        return (
            <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
                {visibleItems.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: '#f9fafb',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#374151',
                        }}
                    >
                        {item}
                    </Box>
                ))}
                {remainingCount > 0 && (
                    <Tooltip
                        title={
                            <Box>
                                {remainingItems.map((item, index) => (
                                    <div key={index}>{item}</div>
                                ))}
                            </Box>
                        }
                        arrow
                        placement="top"
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#e5e7eb',
                                fontSize: '12px',
                                fontWeight: 700,
                                color: '#4b5563',
                                cursor: 'help',
                                '&:hover': {
                                    backgroundColor: '#d1d5db',
                                },
                            }}
                        >
                            +{remainingCount}
                        </Box>
                    </Tooltip>
                )}
            </Box>
        );
    };

    const renderRow = (row, index) => {
        const effectiveStatus = row.transactionStatus || row.status;
        const isCompleted = effectiveStatus === "COMPLETED";
        const isFailed = effectiveStatus === "RETRY" || effectiveStatus === "FAILED";
        const isSourceValidating = row?.sourceValidationStatus === "RUNNING" &&
            ["COMPLETED", "RETRY"].includes(effectiveStatus) &&
            row.run_type === "final";

        const startDateTime = formatDateTime(row?.start_time);
        return (
            <StyledTableRow key={row.transactionId || index}>
                <StyledTableCell>
                    <Box className={classes.brandCell}>
                        <BrandAvatar
                            domainName={row.brandDomainName}
                            brandName={row.brand}
                            className={classes.brandAvatar}
                        />
                        <Box>
                            <div className={classes.brandName} title={row.brand}>
                                {row.brand || "Unknown"}
                                {/* Shared-by badge hidden per client request
                                {row?.is_shared && (
                                    <Chip
                                        label={row?.owner_name ? `Shared by ${row.owner_name}` : "Shared with you"}
                                        size="small"
                                        sx={{ ml: 1, fontSize: "10px", height: "18px", backgroundColor: "#ede9fe", color: "#6d28d9" }}
                                    />
                                )} */}
                            </div>
                            <div className={classes.brandSubtext}>
                                {discoveryType === "brand" ?`${ENTITY_LABELS.brand} Overview`:`${ENTITY_LABELS.product} Overview`}
                            </div>
                            {isSourceValidating && (
                                <div className={classes.validationBadge}>
                                    Sources Validating...
                                </div>
                            )}
                        </Box>
                    </Box>
                </StyledTableCell>

                <StyledTableCell>
                    {row.category && Array.isArray(row.category) ? (
                        renderArrayWithOverflow(row.category, 3)
                    ) : (
                        <span>{row.category?.[0] || ""}</span>
                    )}
                </StyledTableCell>

                <StyledTableCell>
                    {row.llm && Array.isArray(row.llm) ? (
                        renderArrayWithOverflow(
                            row.llm.map(llmValue => {
                                return OVERVIEW_LLM_LABELS[llmValue]
                            //     const llmOption = LLM_OPTIONS.find(opt => opt.value === llmValue);
                            //     return llmOption?.name || llmValue;
                             }),
                            3
                        )
                    ) : (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                backgroundColor: '#f9fafb',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#374151',
                            }}
                        >
                            {OVERVIEW_LLM_LABELS[row?.primaryLLM] || ""}
                        </Box>
                    )}
                </StyledTableCell>

                <StyledTableCell>
                    {row.market && Array.isArray(row.market) ? (
                        renderArrayWithOverflow(
                            row.market.map(m => m === "GeneralMarket" ? "General Market" : m),
                            3
                        )
                    ) : (
                        <span>
                            {row.market?.[0] === "GeneralMarket"
                                ? "General Market"
                                : row.market?.[0] || "General Market"}
                        </span>
                    )}
                </StyledTableCell>

                <StyledTableCell>
                    {row.persona && Array.isArray(row.persona) ? (
                        renderArrayWithOverflow(row.persona, 3)
                    ) : (
                        <span>{row.persona || `General ${ENTITY_LABELS.persona}`}</span>
                    )}
                </StyledTableCell>

                <StyledTableCell title={startDateTime}>
                    {formatDate(row.start_time)}
                </StyledTableCell>

                <StyledTableCell>
                    {getTestRunsBadge(row.total_test_runs, isFailed)}
                </StyledTableCell>

                <StyledTableCell>
                    {getRunTypeChip(row.audit_type || row.run_type)}
                </StyledTableCell>

                <StyledTableCell>{getStatusChip(effectiveStatus)}</StyledTableCell>

                <StyledTableCell>
                    <Box className={classes.actionButtons}>
                        <IconButton
                            size="small"
                            className={classes.actionIcon}
                            onClick={() => handleViewDetails(row)}
                            title={isFailed ? "Retry" : "View Details"}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            className={`${classes.actionIcon} ${classes.deleteIcon}`}
                            onClick={() => handleDeleteClick(row)}
                            title="Delete"
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </StyledTableCell>
            </StyledTableRow>
        );
    };

    const displayData = isSearchMode ? searchResults : history;
    const currentLoading = isSearchMode ? searchLoading : loading;
    const currentTotalCount = isSearchMode ? searchTotalCount : totalCount;
    const currentTotalPages = isSearchMode ? searchTotalPages : totalPages;

    const calculatedTotalPages = currentTotalPages || Math.ceil((currentTotalCount || 0) / PAGE_LIMIT);

    const startIndex = (page - 1) * PAGE_LIMIT;
    const endIndex = Math.min(page * PAGE_LIMIT, currentTotalCount || displayData.length);
    console.log("displayData", displayData)
    return (
        <>
            <Modal
                handleClose={onClose}
                open={open}
                title={`${
                    filterOption === "product"
                        ? ENTITY_LABELS.product
                        : filterOption === "categoryAnalysis"
                            ? ENTITY_LABELS.category
                            : ENTITY_LABELS.brand
                } ${ENTITY_LABELS.audit} History`}
                width="1500"
                showActions={false}
            >
                <Box className={classes.historyContainer}>
                    {/* Header with Search and Refresh */}
                    <Box className={classes.headerContainer}>
                        <TextField
                            placeholder={`Search by ${ENTITY_LABELS.brand}/${ENTITY_LABELS.product}, category, LLM, market, or persona...`}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className={classes.searchIcon} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={handleClearSearch}
                                            edge="end"
                                            className={classes.clearButton}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            className={classes.searchField}
                        />
                        {/* <IconButton
                            onClick={refreshHistory}
                            className={classes.refreshButton}
                            title="Refresh History"
                        >
                            <RefreshIcon />
                        </IconButton> */}
                    </Box>

                    {/* Table */}
                    <Box className={classes.tableWrapper}>
                        {currentLoading && displayData.length === 0 ? (
                            <Box className={classes.loadingContainer}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <CustomizedTables
                                headers={headers}
                                data={displayData}
                                render={renderRow}
                                elevation={0}
                                size="medium"
                            />
                        )}
                    </Box>

                    {/* Pagination Footer */}
                    {(currentTotalCount > 0 || displayData.length > 0) && (
                        <Box className={classes.paginationContainer}>
                            <div className={classes.resultsText}>
                                {currentLoading ? (
                                    <>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Showing{" "}
                                        <span className={classes.boldText}>
                                            {startIndex + 1}
                                        </span>{" "}
                                        to{" "}
                                        <span className={classes.boldText}>
                                            {endIndex}
                                        </span>{" "}
                                        of{" "}
                                        <span className={classes.boldText}>
                                            {currentTotalCount || 0}
                                        </span>{" "}
                                        results
                                        {isSearchMode && (
                                            <span className={classes.searchModeIndicator}> (filtered)</span>
                                        )}
                                    </>
                                )}
                            </div>
                            {calculatedTotalPages > 1 && (
                                <Pagination
                                    count={calculatedTotalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    shape="rounded"
                                    className={classes.pagination}
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                    disabled={currentLoading}
                                />
                            )}
                        </Box>
                    )}


                    {/* Error State */}
                    {error && displayData.length > 0 && (
                        <Box className={classes.errorContainer}>
                            <p className={classes.errorText}>{error}</p>
                        </Box>
                    )}

                    {/* Empty State */}
                    {!currentLoading && !error && displayData.length === 0 && (
                        <Box className={classes.emptyState}>
                            <p>{isSearchMode ? "No results found for your search." : "Nothing here."}</p>
                        </Box>
                    )}

                    {/* Error State - No Data */}
                    {error && displayData.length === 0 && (
                        <Box className={classes.errorState}>
                            <h3>Error</h3>
                            <p>{error}</p>
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                handleClose={() => {
                    setShowDeleteDialog(false);
                    setItemToDelete(null);
                }}
                handleConfirm={deleteHistoryItemHandler}
                open={showDeleteDialog}
                title="Delete History"
                showActions
                confirmBtnText="Delete"
                confirmBtnColor="error"
            >
                <div className={classes.contentText}>
                    Do you want to delete this item - {itemToDelete?.brand}?
                </div>
            </Modal>
        </>
    );
};

export default HistoryV2;